import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { toast } from 'sonner';

const Login = ({ onLogin }) => {
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({
    mobile: '',
    password: '',
    name: '',
    email: '',
  });
  const [countryCode, setCountryCode] = useState('+91');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      if (isRegister) {
        await axios.post(`${API}/auth/register`, {
          mobile: `${countryCode}${formData.mobile}`,
          password: formData.password,
          name: formData.name,
          email: formData.email || null,
        });
        toast.success('Registration successful! Please login.');
        setIsRegister(false);
        setFormData({ mobile: '', password: '', name: '', email: '' });
      } else {
        const response = await axios.post(`${API}/auth/login`, {
          mobile: `${countryCode}${formData.mobile}`,
          password: formData.password,
        });
        onLogin(response.data.token, response.data.user);
        toast.success('Login successful!');
      }
    } catch (error) {
      const msg = error.response?.data?.detail || (error.response?.status === 401 ? 'Invalid mobile number or password' : 'An error occurred during login');
      setErrorMessage(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center relative"
      style={{
        backgroundImage: 'url(https://images.pexels.com/photos/4481256/pexels-photo-4481256.jpeg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
      data-testid="login-page"
    >
      <div className="absolute inset-0 bg-black/50" />

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white p-8 rounded-sm shadow-md border border-stone-200" data-testid="login-form">
          <h2
            className="text-3xl font-semibold text-[hsl(145,60%,20%)] mb-6 tracking-tight"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            data-testid="form-title"
          >
            {isRegister ? 'Register' : 'Login'}
          </h2>

          {errorMessage && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded" data-testid="login-error-alert">
              {errorMessage}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {isRegister && (
              <div>
                <Label htmlFor="name" data-testid="label-name">Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required={isRegister}
                  className="mt-1"
                  data-testid="input-name"
                />
              </div>
            )}

            <div>
              <Label htmlFor="mobile" data-testid="label-mobile">Mobile Number</Label>
              <PhoneInput
                value={formData.mobile}
                onChange={(val) => setFormData({ ...formData, mobile: val })}
                countryCode={countryCode}
                onCountryCodeChange={setCountryCode}
              />
            </div>

            {isRegister && (
              <div>
                <Label htmlFor="email" data-testid="label-email">Email (Optional)</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email address"
                  className="mt-1"
                  data-testid="input-email"
                />
              </div>
            )}

            <div>
              <Label htmlFor="password" data-testid="label-password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                className="mt-1"
                data-testid="input-password"
              />
              {!isRegister && (
                <Link
                  to="/forgot-password"
                  className="text-sm text-[hsl(145,60%,20%)] hover:underline mt-1 inline-block"
                  data-testid="forgot-password-link"
                >
                  Forgot Password?
                </Link>
              )}
            </div>

            <Button
              type="submit"
              className="w-full bg-[hsl(145,60%,20%)] hover:bg-[hsl(145,60%,15%)] text-white"
              disabled={loading}
              data-testid="submit-button"
            >
              {loading ? 'Please wait...' : isRegister ? 'Register' : 'Login'}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => setIsRegister(!isRegister)}
              className="text-sm text-[hsl(145,60%,20%)] hover:underline"
              data-testid="toggle-form-button"
            >
              {isRegister ? 'Already have an account? Login' : "Don't have an account? Register"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;